import { APERCU } from './apercu'
import {
  lireEpisode,
  lireEtatMeteo,
  lireReglagesMeteo,
  messageMeteo,
  type DonneesMeteo,
  type ModeMeteo,
} from './meteo'
import { appelerFonction, supabase } from './supabase'

/**
 * Météo automatique (0061) — accès à la base depuis la console dispatch : lecture
 * (staff), RPC Arrêter / Forcer, réglages (gerer_config), « Vérifier maintenant »
 * (edge function `meteo-auto`), temps réel. Aucune requête météo depuis le navigateur.
 */

type Ligne = Record<string, unknown>

const tableAbsente = (e: { code?: string; message?: string }) =>
  e.code === '42P01' || e.code === 'PGRST205' || /could not find the table|does not exist/i.test(e.message ?? '')

// ── Aperçu de développement (jamais livré) ─────────────────────────────

let apercu: DonneesMeteo | null = null
function donneesApercu(): DonneesMeteo {
  const maintenant = Date.now()
  apercu ??= {
    reglages: {
      mode: 'auto',
      points: [
        { code: 'centre', nom: 'Centre-ville', lat: 12.6392, lon: -8.0029 },
        { code: 'rg_nord_ouest', nom: 'Rive gauche nord-ouest', lat: 12.655, lon: -8.045 },
        { code: 'rg_nord_est', nom: 'Rive gauche nord-est', lat: 12.66, lon: -7.955 },
        { code: 'rd_nord', nom: 'Rive droite nord', lat: 12.615, lon: -7.97 },
        { code: 'rd_sud', nom: 'Rive droite sud', lat: 12.565, lon: -7.99 },
      ],
      pluieSeuilMm: 0.3,
      pluiePointsMin: 2,
      pluieFinMinutes: 30,
      chaleurActivationC: 40,
      chaleurDesactivationC: 38,
      pointChaleur: 'centre',
      echecsMax: 3,
      pushLivreurs: false,
    },
    etat: {
      pluieActive: false, pluieDepuis: null, bloquePluie: false,
      chaleurActive: false, chaleurDepuis: null, bloqueChaleur: false,
      forceCondition: null, forceJusqua: null, forceDepuis: null,
      echecsConsecutifs: 0, sourceIndisponible: false, derniereErreur: null,
      dernierReleve: new Date(maintenant - 4 * 60_000).toISOString(),
      dernierSucces: new Date(maintenant - 4 * 60_000).toISOString(),
      prochaineVerification: new Date(maintenant + 6 * 60_000).toISOString(),
      releves: [
        { code: 'centre', nom: 'Centre-ville', ok: true, pluie: false, precipitationMm: 0, symbole: 'partlycloudy_day', temperatureC: 34.2, erreur: null },
        { code: 'rg_nord_ouest', nom: 'Rive gauche nord-ouest', ok: true, pluie: true, precipitationMm: 0.8, symbole: 'lightrainshowers_day', temperatureC: 31, erreur: null },
        { code: 'rg_nord_est', nom: 'Rive gauche nord-est', ok: true, pluie: false, precipitationMm: 0, symbole: 'cloudy', temperatureC: 33.1, erreur: null },
        { code: 'rd_nord', nom: 'Rive droite nord', ok: true, pluie: false, precipitationMm: 0.1, symbole: 'cloudy', temperatureC: 33.8, erreur: null },
        { code: 'rd_sud', nom: 'Rive droite sud', ok: false, pluie: false, precipitationMm: null, symbole: null, temperatureC: null, erreur: 'HTTP 503' },
      ],
      source: 'met_norway',
    },
    episodes: [
      {
        id: 'e1', condition: 'rain', debut: new Date(maintenant - 26 * 3600_000).toISOString(),
        fin: new Date(maintenant - 25 * 3600_000).toISOString(), origineDebut: 'automate', debutPar: null,
        motifDebut: 'Pluie annoncée sur 3 points sur 5 (Centre-ville 1,4 mm heavyrainshowers_day, …)', forceJusqua: null,
        origineFin: 'staff', finPar: null, motifFin: 'Arrêt par le dispatch : il ne pleut plus',
      },
    ],
    noms: {},
  }
  return apercu
}

// ── Accès à la base ────────────────────────────────────────────────────

/** Charge réglages, état et 15 derniers épisodes ; null = migration 0061 absente. */
export async function chargerMeteo(): Promise<DonneesMeteo | null> {
  if (APERCU) return structuredClone(donneesApercu())
  const [reg, etat, eps] = await Promise.all([
    supabase.from('meteo_reglages').select('*').eq('id', 1).maybeSingle(),
    supabase.from('meteo_etat').select('*').eq('id', 1).maybeSingle(),
    supabase.from('meteo_episodes').select('*').order('debut', { ascending: false }).limit(15),
  ])
  for (const r of [reg, etat, eps]) {
    if (r.error) {
      if (tableAbsente(r.error)) return null
      throw new Error(r.error.message)
    }
  }
  if (!reg.data || !etat.data) return null
  const episodes = ((eps.data ?? []) as Ligne[]).map(lireEpisode)
  const ids = [...new Set(episodes.flatMap((e) => [e.debutPar, e.finPar]).filter((x): x is string => x !== null))]
  const noms: Record<string, string> = {}
  if (ids.length) {
    const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids)
    for (const p of (data ?? []) as { id: string; full_name: string | null }[]) noms[p.id] = p.full_name || 'Membre du staff'
  }
  return {
    reglages: lireReglagesMeteo(reg.data as Ligne),
    etat: lireEtatMeteo(etat.data as Ligne),
    episodes,
    noms,
  }
}

/** Le dispatch constate qu'il ne pleut plus (ou que la chaleur est retombée). */
export async function arreterMeteo(motif?: string): Promise<'ok' | 'rien_a_arreter'> {
  if (APERCU) {
    const d = donneesApercu()
    d.etat = { ...d.etat, forceCondition: null, forceJusqua: null, bloquePluie: d.etat.pluieActive || d.etat.bloquePluie, pluieActive: false }
    return 'ok'
  }
  const { data, error } = await supabase.rpc('meteo_arreter', { p_motif: motif ?? null })
  if (error) throw new Error(messageMeteo(error.message))
  return (data as { resultat?: string } | null)?.resultat === 'rien_a_arreter' ? 'rien_a_arreter' : 'ok'
}

/** Forcer pluie / forte chaleur pour `minutes` (null = jusqu'à arrêt). */
export async function forcerMeteo(condition: 'rain' | 'extreme_heat', minutes: number | null, motif?: string): Promise<void> {
  if (APERCU) {
    const d = donneesApercu()
    d.etat = {
      ...d.etat,
      forceCondition: condition,
      forceJusqua: minutes === null ? null : new Date(Date.now() + minutes * 60_000).toISOString(),
      forceDepuis: new Date().toISOString(),
    }
    return
  }
  const { error } = await supabase.rpc('meteo_forcer', { p_condition: condition, p_minutes: minutes, p_motif: motif ?? null })
  if (error) throw new Error(messageMeteo(error.message))
}

/** Mode automatique / manuel (droit `gerer_config`). */
export async function changerModeMeteo(mode: ModeMeteo): Promise<void> {
  if (APERCU) {
    donneesApercu().reglages.mode = mode
    return
  }
  const { data, error } = await supabase.from('meteo_reglages').update({ mode }).eq('id', 1).select('id')
  if (error) throw new Error(messageMeteo(error.message))
  if (!data?.length) throw new Error("Vous n'avez pas le droit de changer le mode météo (réservé à l'administrateur).")
}

export async function enregistrerReglagesMeteo(ligne: Record<string, unknown>): Promise<void> {
  if (APERCU) return
  const { data, error } = await supabase.from('meteo_reglages').update(ligne).eq('id', 1).select('id')
  if (error) throw new Error(messageMeteo(error.message))
  if (!data?.length) throw new Error("Vous n'avez pas le droit de modifier ces réglages (réservé à l'administrateur).")
}

/** « Vérifier maintenant » : relance l'automate (le cache MET Norway s'applique). */
export async function verifierMeteoMaintenant(): Promise<void> {
  if (APERCU) return
  await appelerFonction('meteo-auto', {})
}

/**
 * Temps réel : l'automate a relevé (meteo_etat) ou un épisode a commencé /
 * fini (meteo_episodes). Le rappel reçoit la table touchée.
 */
export function suivreMeteo(surChangement: (table: 'meteo_etat' | 'meteo_episodes') => void): () => void {
  if (APERCU) return () => {}
  const canal = supabase
    .channel('dispatch-meteo')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'meteo_etat' }, () => surChangement('meteo_etat'))
    .on('postgres_changes', { event: '*', schema: 'public', table: 'meteo_episodes' }, () => surChangement('meteo_episodes'))
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}
